import { useEffect, useRef, useState } from "react";
import Pusher from "pusher-js";
import { useDispatch } from "react-redux";
import {
  addChatNotificationList,
  addNotificationList,
  addFailedOrderNotificationList,
} from "./../redux/counterSlice";
import toast from "react-hot-toast";
import notificationSound from "./../assets/audio/new_order_received.mp3";
import chatNotification from "./../assets/audio/message-incoming.mp3";
import failedOrderSound from "./../assets/audio/order_fail.wav";

const usePusherReceiver = () => {
  const dispatch = useDispatch();
  const [audioEnabled, setAudioEnabled] = useState(false);
  const enableAudioHandlerRef = useRef(() => {});

  const audioRef = useRef(new Audio(notificationSound));
  const chatAudioRef = useRef(new Audio(chatNotification));
  const failedAudioRef = useRef(new Audio(failedOrderSound));

  const audioUnlockedRef = useRef(false);
  const chatAudioUnlockedRef = useRef(false);
  const failedAudioUnlockedRef = useRef(false);
  const audioUnlockingRef = useRef(false);
  const chatAudioUnlockingRef = useRef(false);
  const failedAudioUnlockingRef = useRef(false);
  const pendingOrderSoundRef = useRef(false);
  const pendingFailedSoundRef = useRef(false);
  const alertedOrderIdsRef = useRef(new Set());

  useEffect(() => {
    audioRef.current.load();
    chatAudioRef.current.load();
    failedAudioRef.current.load();

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: "ap2",
    });

    const showBrowserNotification = (title, body) => {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body });
      }
    };

    const playSoundWithFallback = () => {
      if (audioUnlockedRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          audioUnlockedRef.current = false;
          setAudioEnabled(false);
          pendingOrderSoundRef.current = true;
          showBrowserNotification("🛒 New Order Received", "Check your dashboard for details.");
        });
      } else {
        pendingOrderSoundRef.current = true;
        showBrowserNotification("🛒 New Order Received", "Check your dashboard for details.");
      }
    };

    const playChatSoundWithFallback = () => {
      if (chatAudioUnlockedRef.current) {
        chatAudioRef.current.play().catch(() => {
          showBrowserNotification("New Chat Message", "You've received a new customer message.");
        });
      } else {
        showBrowserNotification("New Chat Message", "You've received a new customer message.");
      }
    };

    const playFailedOrderSound = () => {
      if (!failedAudioUnlockedRef.current) {
        pendingFailedSoundRef.current = true;
        showBrowserNotification("⚠️ Failed Order Alert", "A new failed order requires attention!");
        return;
      }

      failedAudioRef.current.currentTime = 0;
      failedAudioRef.current.play().catch(() => {
        failedAudioUnlockedRef.current = false;
        pendingFailedSoundRef.current = true;
        showBrowserNotification("⚠️ Failed Order Alert", "A new failed order requires attention!");
      });
    };

    const unlockAudio = () => {
      const unlock = (audio, onUnlocked, isUnlocking, pendingAlertRef = null) => {
        if (onUnlocked.current || isUnlocking.current) {
          return;
        }

        isUnlocking.current = true;
        audio.currentTime = 0;
        const playback = audio.play();
        playback
          .then(() => {
            isUnlocking.current = false;
            onUnlocked.current = true;
            if (audio === audioRef.current) {
              setAudioEnabled(true);
            }

            if (pendingAlertRef?.current) {
              pendingAlertRef.current = false;
              return;
            }

            audio.pause();
            audio.currentTime = 0;

            if (audio === failedAudioRef.current && pendingFailedSoundRef.current) {
              audio.play().then(() => {
                pendingFailedSoundRef.current = false;
              }).catch(() => {
                onUnlocked.current = false;
                pendingFailedSoundRef.current = true;
                showBrowserNotification(
                  "⚠️ Failed Order Alert",
                  "A new failed order requires attention!",
                );
              });
            }
          })
          .catch(() => {
            isUnlocking.current = false;
            onUnlocked.current = false;
            if (audio === audioRef.current) {
              setAudioEnabled(false);
            }
          });
      };

      unlock(audioRef.current, audioUnlockedRef, audioUnlockingRef, pendingOrderSoundRef);
      unlock(chatAudioRef.current, chatAudioUnlockedRef, chatAudioUnlockingRef);
      unlock(
        failedAudioRef.current,
        failedAudioUnlockedRef,
        failedAudioUnlockingRef,
        pendingFailedSoundRef,
      );
    };

    enableAudioHandlerRef.current = unlockAudio;
    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    const adminChannel = pusher.subscribe("blaack-forest-app-channel");
    adminChannel.bind("admin-event", (data) => {
      dispatch(addNotificationList(data));
      playSoundWithFallback();
    });

    const chatChannel = pusher.subscribe("blaack-forest-chat-channel");
    chatChannel.bind("chat-event", (data) => {
      dispatch(addChatNotificationList(data));
      playChatSoundWithFallback();
    });

    const failedChannel = pusher.subscribe("blaack-forest-failed-channel");
    failedChannel.bind("failed-order-event", (data) => {
      dispatch(addFailedOrderNotificationList(data));

      const orderId = data?.id;
      const hasAlreadyAlerted =
        orderId != null && alertedOrderIdsRef.current.has(String(orderId));

      if (!hasAlreadyAlerted) {
        if (orderId != null) {
          alertedOrderIdsRef.current.add(String(orderId));
        }
        playFailedOrderSound();
        toast.error(`⚠️ Failed Order #${orderId || ""} - Needs Attention!`, {
          duration: 6000,
        });
      }
    });

    return () => {
      enableAudioHandlerRef.current = () => {};
      pusher.unsubscribe("blaack-forest-app-channel");
      pusher.unsubscribe("blaack-forest-chat-channel");
      pusher.unsubscribe("blaack-forest-failed-channel");
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, [dispatch]);

  return {
    audioEnabled,
    enableOrderSound: () => enableAudioHandlerRef.current(),
  };
};

export default usePusherReceiver;